import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Edit, Trash2, Calendar } from 'lucide-react';

export default function MonthlyDataTable({ 
  records, 
  onEdit, 
  onDelete, 
  formatCurrency,
  calculateMonthlyProfitLoss,
  calculateROI
}) {
  const [expandedRows, setExpandedRows] = useState(new Set());

  const toggleRow = (recordId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded?.has(recordId)) {
      newExpanded?.delete(recordId);
    } else {
      newExpanded?.add(recordId);
    }
    setExpandedRows(newExpanded);
  };

  const calculateROAS = (record) => {
    // Use database ROAS if available, otherwise calculate
    if (record?.roas && record?.roas > 0) {
      return record?.roas;
    }
    
    const ppcSpend = parseFloat(record?.googleAdsExpense || 0);
    const totalRevenue = parseFloat(record?.existingClientRevenue || 0) + parseFloat(record?.newClientRevenue || 0);
    
    if (ppcSpend === 0) return 0;
    return totalRevenue / ppcSpend;
  };

  if (records?.length === 0) {
    return (
      <div className="text-center py-16 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border-2 border-dashed border-gray-300">
        <Calendar size={64} className="mx-auto text-gray-400 mb-4" />
        <p className="text-lg text-gray-600 mb-2 font-medium">No monthly performance data yet</p>
        <p className="text-sm text-gray-500">Start tracking your sales performance to see insights and trends</p>
      </div>
    );
  }

  // Calculate totals
  const totals = {
    totalClients: records?.reduce((sum, r) => sum + ((r?.existingClientSales || 0) + (r?.newClientSales || 0)), 0),
    existingClients: records?.reduce((sum, r) => sum + (r?.existingClientSales || 0), 0),
    newClients: records?.reduce((sum, r) => sum + (r?.newClientSales || 0), 0),
    totalRevenue: records?.reduce((sum, r) => sum + ((r?.existingClientRevenue || 0) + (r?.newClientRevenue || 0)), 0),
    existingRevenue: records?.reduce((sum, r) => sum + (r?.existingClientRevenue || 0), 0),
    newRevenue: records?.reduce((sum, r) => sum + (r?.newClientRevenue || 0), 0),
    agencyCosts: records?.reduce((sum, r) => sum + (r?.otherMonthlyCosts || 0), 0),
    ppcCosts: records?.reduce((sum, r) => sum + (r?.googleAdsExpense || 0), 0),
    totalExpenses: records?.reduce((sum, r) => sum + (r?.otherMonthlyCosts || 0) + (r?.googleAdsExpense || 0), 0),
    netProfit: records?.reduce((sum, r) => {
      const calc = calculateMonthlyProfitLoss(r);
      return sum + calc?.netProfit;
    }, 0),
    totalInvestment: 0,
    averageROI: 0,
    averageROAS: 0
  };

  // Calculate total investment and average ROI
  totals.totalInvestment = totals?.agencyCosts + totals?.ppcCosts;
  if (totals?.totalInvestment > 0) {
    totals.averageROI = ((totals?.totalRevenue - totals?.totalInvestment) / totals?.totalInvestment) * 100;
  } else {
    totals.averageROI = totals?.totalRevenue > 0 ? 100 : 0;
  }

  // Calculate average ROAS across all records
  const roasValues = records
    ?.filter(r => calculateROAS(r) > 0)
    ?.map(r => calculateROAS(r));
  
  if (roasValues?.length > 0) {
    totals.averageROAS = roasValues?.reduce((sum, val) => sum + val, 0) / roasValues?.length;
  }

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
          <p className="text-xs font-medium text-blue-700 uppercase tracking-wide mb-1">Total Clients/Orders</p>
          <p className="text-3xl font-bold text-blue-900">{totals?.totalClients}</p>
          <div className="flex items-center gap-2 mt-2 text-xs text-blue-600">
            <span>Existing: {totals?.existingClients}</span>
            <span>•</span>
            <span>New: {totals?.newClients}</span>
          </div>
        </div>

        <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-lg p-4 border border-emerald-200">
          <p className="text-xs font-medium text-emerald-700 uppercase tracking-wide mb-1">Total Revenue</p>
          <p className="text-3xl font-bold text-emerald-900">{formatCurrency(totals?.totalRevenue)}</p>
          <div className="flex items-center gap-2 mt-2 text-xs text-emerald-600">
            <span>Existing: {formatCurrency(totals?.existingRevenue)}</span>
          </div>
        </div>

        <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-lg p-4 border-2 border-red-300 shadow-md">
          <p className="text-xs font-bold text-red-700 uppercase tracking-wide mb-1 flex items-center gap-1">
            <span>💰</span>
            <span>Total Expenses</span>
          </p>
          <p className="text-3xl font-bold text-red-900">{formatCurrency(totals?.totalExpenses)}</p>
          <div className="flex flex-col gap-1 mt-2 text-xs text-red-600">
            <div className="flex justify-between">
              <span>Agency Fees:</span>
              <span className="font-semibold">{formatCurrency(totals?.agencyCosts)}</span>
            </div>
            <div className="flex justify-between">
              <span>PPC Costs:</span>
              <span className="font-semibold">{formatCurrency(totals?.ppcCosts)}</span>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-cyan-50 to-cyan-100 rounded-lg p-4 border border-cyan-200">
          <p className="text-xs font-medium text-cyan-700 uppercase tracking-wide mb-1">Average ROAS</p>
          <p className="text-3xl font-bold text-cyan-900">
            {totals?.averageROAS?.toFixed(2)}x
          </p>
          <div className={`flex items-center gap-2 mt-2 text-xs font-semibold ${
            totals?.averageROAS >= 4 ? 'text-green-600' :
            totals?.averageROAS >= 2 ? 'text-yellow-600' : 'text-red-600'
          }`}>
            {totals?.averageROAS >= 4 ? '✓ Excellent' :
             totals?.averageROAS >= 2 ? '⚠ Acceptable' : '✗ Below Target'}
          </div>
        </div>

        <div className={`bg-gradient-to-br rounded-lg p-4 border ${
          totals?.averageROI >= 0 
            ? 'from-indigo-50 to-indigo-100 border-indigo-200' :'from-amber-50 to-amber-100 border-amber-200'
        }`}>
          <p className={`text-xs font-medium uppercase tracking-wide mb-1 ${
            totals?.averageROI >= 0 ? 'text-indigo-700' : 'text-amber-700'
          }`}>
            Average ROI
          </p>
          <p className={`text-3xl font-bold ${
            totals?.averageROI >= 0 ? 'text-indigo-900' : 'text-amber-900'
          }`}>
            {totals?.averageROI?.toFixed(1)}%
          </p>
          <div className={`flex items-center gap-2 mt-2 text-xs font-semibold ${
            totals?.averageROI >= 0 ? 'text-indigo-600' : 'text-amber-600'
          }`}>
            {totals?.averageROI >= 0 ? '✓ Positive ROI' : '✗ Negative ROI'}
          </div>
        </div>

        <div className={`bg-gradient-to-br rounded-lg p-4 border ${
          totals?.netProfit >= 0 
            ? 'from-green-50 to-green-100 border-green-200' :'from-orange-50 to-orange-100 border-orange-200'
        }`}>
          <p className={`text-xs font-medium uppercase tracking-wide mb-1 ${
            totals?.netProfit >= 0 ? 'text-green-700' : 'text-orange-700'
          }`}>
            {totals?.netProfit >= 0 ? 'Net Profit' : 'Net Loss'}
          </p>
          <p className={`text-3xl font-bold ${
            totals?.netProfit >= 0 ? 'text-green-900' : 'text-orange-900'
          }`}>
            {formatCurrency(Math.abs(totals?.netProfit))}
          </p>
          <div className={`flex items-center gap-2 mt-2 text-xs font-semibold ${
            totals?.netProfit >= 0 ? 'text-green-600' : 'text-orange-600'
          }`}>
            {totals?.netProfit >= 0 ? '✓ Profitable Period' : '✗ Loss Period'}
          </div>
        </div>
      </div>
      {/* Monthly Data Rows */}
      <div className="space-y-3">
        {records?.map((record) => {
          const profitData = calculateMonthlyProfitLoss(record);
          const roi = calculateROI(record);
          const isExpanded = expandedRows?.has(record?.id);
          const totalClients = (record?.existingClientSales || 0) + (record?.newClientSales || 0);
          const totalRevenue = profitData?.totalRevenue;
          const totalExpenses = (record?.otherMonthlyCosts || 0) + (record?.googleAdsExpense || 0);

          return (
            <div 
              key={record?.id} 
              className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden"
            >
              {/* Collapsed View - Summary Only */}
              <div 
                className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => toggleRow(record?.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 flex-1">
                    {/* Month */}
                    <div className="min-w-[100px]">
                      <p className="text-sm font-medium text-gray-500 mb-1">Month</p>
                      <p className="text-lg font-bold text-gray-900">
                        {new Date(record?.trackingMonth)?.toLocaleDateString('en-US', { year: 'numeric', month: 'short' })}
                      </p>
                    </div>

                    {/* Quick Stats */}
                    <div className="flex items-center gap-6 flex-1">
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Clients</p>
                        <p className="text-base font-semibold text-blue-700">{totalClients}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Revenue</p>
                        <p className="text-base font-semibold text-emerald-700">{formatCurrency(totalRevenue)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Expenses</p>
                        <p className="text-base font-semibold text-red-700">{formatCurrency(totalExpenses)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">ROAS</p>
                        <p className={`text-base font-semibold ${
                          calculateROAS(record) >= 4 ? 'text-green-700' :
                          calculateROAS(record) >= 2 ? 'text-yellow-700' : 'text-red-700'
                        }`}>
                          {calculateROAS(record)?.toFixed(2)}x
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">ROI</p>
                        <p className={`text-base font-semibold ${
                          roi >= 0 ? 'text-indigo-700' : 'text-amber-700'
                        }`}>
                          {roi?.toFixed(1)}%
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Net Profit</p>
                        <p className={`text-base font-semibold ${
                          profitData?.isProfit ? 'text-green-700' : 'text-red-700'
                        }`}>
                          {formatCurrency(profitData?.netProfit)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Status</p>
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                          profitData?.isProfit 
                            ? 'bg-green-100 text-green-800' :'bg-red-100 text-red-800'
                        }`}>
                          {profitData?.isProfit ? '✓ Profit' : '✗ Loss'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Expand/Collapse Button */}
                  <div className="flex items-center gap-3">
                    <button
                      onClick={(e) => {
                        e?.stopPropagation();
                        onEdit(record);
                      }}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Edit"
                    >
                      <Edit size={18} />
                    </button>
                    <button
                      onClick={(e) => {
                        e?.stopPropagation();
                        onDelete(record?.id);
                      }}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete"
                    >
                      <Trash2 size={18} />
                    </button>
                    <div className="text-gray-400">
                      {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </div>
                  </div>
                </div>
              </div>
              {/* Expanded View - Detailed Breakdown */}
              {isExpanded && (
                <div className="border-t border-gray-200 bg-gray-50 p-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Client Details */}
                    <div className="space-y-3">
                      <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                        Client Breakdown
                      </h3>
                      <div className="space-y-2 bg-white rounded-lg p-3 border border-blue-100">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Existing Clients</span>
                          <span className="text-sm font-semibold text-blue-700">{record?.existingClientSales || 0}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">New Clients</span>
                          <span className="text-sm font-semibold text-blue-700">{record?.newClientSales || 0}</span>
                        </div>
                        <div className="flex justify-between items-center pt-2 border-t border-blue-100">
                          <span className="text-sm font-medium text-gray-900">Total</span>
                          <span className="text-base font-bold text-blue-900">{totalClients}</span>
                        </div>
                      </div>
                    </div>

                    {/* Revenue Details */}
                    <div className="space-y-3">
                      <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                        Revenue Breakdown
                      </h3>
                      <div className="space-y-2 bg-white rounded-lg p-3 border border-emerald-100">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Existing Clients</span>
                          <span className="text-sm font-semibold text-emerald-700">
                            {formatCurrency(record?.existingClientRevenue || 0)}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">New Clients</span>
                          <span className="text-sm font-semibold text-emerald-700">
                            {formatCurrency(record?.newClientRevenue || 0)}
                          </span>
                        </div>
                        <div className="flex justify-between items-center pt-2 border-t border-emerald-100">
                          <span className="text-sm font-medium text-gray-900">Total Revenue</span>
                          <span className="text-base font-bold text-emerald-900">
                            {formatCurrency(totalRevenue)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Cost & Profit Details */}
                    <div className="space-y-3">
                      <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                        Financial Summary
                      </h3>
                      <div className="space-y-2 bg-white rounded-lg p-3 border border-purple-100">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Agency Fees</span>
                          <span className="text-sm font-semibold text-red-600">
                            {formatCurrency(record?.otherMonthlyCosts || 0)}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">PPC Fees</span>
                          <span className="text-sm font-semibold text-red-600">
                            {formatCurrency(record?.googleAdsExpense || 0)}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Gross Profit %</span>
                          <span className="text-sm font-semibold text-gray-700">
                            {record?.grossProfitPercentage || 0}%
                          </span>
                        </div>
                        <div className="flex justify-between items-center pt-2 border-t border-purple-100">
                          <span className="text-sm font-medium text-gray-900">ROAS</span>
                          <span className={`text-base font-bold ${
                            calculateROAS(record) >= 4 ? 'text-green-700' :
                            calculateROAS(record) >= 2 ? 'text-yellow-700' : 'text-red-700'
                          }`}>
                            {calculateROAS(record)?.toFixed(2)}x
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-gray-900">ROI</span>
                          <span className={`text-base font-bold ${
                            roi >= 0 ? 'text-indigo-700' : 'text-amber-700'
                          }`}>
                            {roi?.toFixed(1)}%
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-gray-900">Net Profit</span>
                          <span className={`text-base font-bold ${
                            profitData?.isProfit ? 'text-green-700' : 'text-red-700'
                          }`}>
                            {formatCurrency(profitData?.netProfit)}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-gray-900">Margin</span>
                          <span className={`text-sm font-semibold ${
                            profitData?.isProfit ? 'text-green-700' : 'text-red-700'
                          }`}>
                            {profitData?.profitMarginPercentage?.toFixed(1)}%
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Notes Section */}
                  {record?.notes && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <h3 className="text-sm font-semibold text-gray-700 mb-2">Notes</h3>
                      <p className="text-sm text-gray-600 bg-yellow-50 p-3 rounded border border-yellow-200">
                        {record?.notes}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}